// Copyright (c) 2023, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import "../styles/CaseView.css";
import { useContext, useEffect } from 'react';
import { useGetApi } from '../data/hooks/api';
import { RESOURCE_SERVER_URLS } from '../data/configs/spl-config';
import * as Types from "../data/utils/types";
import { useAuthContext } from "@asgardeo/auth-react";
import Loading from "./LinearLoading";
import ErrorIcon from '@mui/icons-material/Error';
import Tooltip from "@mui/material/Tooltip";
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { Box, Typography } from '@mui/material';
import { PermissionContext } from './Authorize';

type AttachmentBoxProps = {
    caseId: string | undefined;
}

export const AttachmentBox = (props: AttachmentBoxProps) => {

    const requestURL = `/cases/${props.caseId}/attachments-info?offset=0&limit=10`;
    const { httpRequest } = useAuthContext();
    const apiUrl = RESOURCE_SERVER_URLS[0] + requestURL;
    const { data, loading, error, getApiData } = useGetApi<Types.caseCommentDetails[]>({ url: apiUrl, headers: { "accept": "application/json" } });
    const authInfo = useContext(PermissionContext);

    useEffect(() => {
        getApiData();
    }, []);

    const attachmentDownload = async (attachment_ID: string, file_name: string) => {
        const attachmentDownloadUrl = RESOURCE_SERVER_URLS[0] + `/attachments/${attachment_ID}/download`;
        try {
            const response = await httpRequest({
                url: attachmentDownloadUrl,
                method: 'GET',
                headers: { "accept": "application/json" },
                responseType: 'arraybuffer',
            });
            const blob = new Blob([response.data], { type: response.headers['content-type'] });
            const href = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = href;
            link.download = file_name;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(href);
        } catch (error) {
            console.error(error);
        }
    }

    return (
        loading ? <Loading />
            : error ? (
                <Box display="flex" alignItems="center" gap={1} color="error">
                  <ErrorIcon color='error' />
                  <Typography color="red" variant="body1">error while loading attachments!</Typography>
                </Box>
              ) : (
                <div className="card">
                    {data && (
                        data.length == 0 ?
                            (<div className="attachment-card-body" >
                                <div className='attachment-not-found'>
                                    <p>No attachments</p>
                                </div>
                            </div>) : authInfo.isUserAllowedtoDownloadAttachments? 
                            (data.map((item, index) => (
                                <div key={index} className="attachment-card-body" >
                                    <div className='attachment-items' onClick={() => attachmentDownload(item.sysId, item.fileName)} >
                                        <p className="attachment-item-filename">{item.fileName}</p>
                                        <AccessTimeIcon fontSize="small" style={{margin:"0.5rem 0",}} /> 
                                        <p className="attachment-item-time">
                                        {new Date(item.createdOn + "Z").toLocaleString()}
                                        </p>
                                    </div>
                                </div>))
                            ) : 
                            (data.map((item, index) => (        
                                <div key={index} className="attachment-card-body" >
                                    <Tooltip title="You don't have permission to download this file"  >
                                        <div className='attachment-items-disabled' >
                                        <p style={{margin:"0.5rem auto"}}>{item.fileName}</p>
                                        <AccessTimeIcon fontSize="small" style={{margin:"0.5rem 0",}} /> 
                                        <p className="attachment-item-time">
                                        {new Date(item.createdOn + "Z").toLocaleString()}
                                        </p>
                                      </div>
                                    </Tooltip>
                                </div>
                                ))
                            )
                            )
                    }
                </div>
            )
    )
}
